import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import CompanyCreate from "./CompanyCreate";
import CompanyDetail from "./CompanyDetail";
import CompanyDeleteDialog from "./dialog/CompanyDeleteDialog";
import { getCompanyList, postCompanyUpdate } from "../services/callApi";
import {
  getApiStatus,
  getManagementApiErrorMessage,
} from "../utils/managementApiError";
import { API_QUERY_KEYS, invalidateApiQueries } from "../utils/apiQueryCache";
import {
  MANAGEMENT_ACTION_CELL_SX,
  MANAGEMENT_BODY_CELL_SX,
  MANAGEMENT_HEADER_CELL_SX,
  MANAGEMENT_ROWS_PER_PAGE,
  MANAGEMENT_SPACER_BODY_CELL_SX,
  MANAGEMENT_SPACER_HEADER_CELL_SX,
  MANAGEMENT_TABLE_SX,
  ManagementEmptyState,
  ManagementIconActionButton,
  ManagementListPage,
  ManagementListPagination,
  ManagementTableContainer,
  ManagementTruncatedText,
} from "./common/ManagementListLayout";

export const buildCompanyDeactivatePayload = (company) => ({
  company_id: company.companyId,
  company_name: company.companyName,
  exam_password: company.examPassword,
  is_active: false,
});

function CompanyList({ role, onForbidden }) {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [page, setPage] = useState(0);
  const canDeactivate = role === "super_admin";

  const handleApiError = useCallback(
    (error) => {
      const status = getApiStatus(error);
      if (status === 401) {
        window.location.href = "/login";
        return;
      }
      if (status === 403) {
        onForbidden("companyList");
        return;
      }
      setErrorMessage(getManagementApiErrorMessage(error));
    },
    [onForbidden]
  );

  const loadCompanies = useCallback(
    async (selectedId, showLoading = false) => {
      if (showLoading) {
        setLoading(true);
      }

      try {
        const response = await getCompanyList();
        const nextCompanies = Array.isArray(response)
          ? [...response].sort((a, b) => a.companyId - b.companyId)
          : [];
        setCompanies(nextCompanies);
        if (selectedId != null) {
          setSelectedCompany(
            nextCompanies.find((company) => company.companyId === selectedId) ?? null
          );
        }
        setErrorMessage("");
        return nextCompanies;
      } catch (error) {
        handleApiError(error);
        return null;
      } finally {
        if (showLoading) {
          setLoading(false);
        }
      }
    },
    [handleApiError]
  );

  useEffect(() => {
    loadCompanies(null, true);
  }, [loadCompanies]);

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(companies.length / MANAGEMENT_ROWS_PER_PAGE) - 1);
    setPage((currentPage) => Math.min(currentPage, lastPage));
  }, [companies.length]);

  const paginatedCompanies = companies.slice(
    page * MANAGEMENT_ROWS_PER_PAGE,
    page * MANAGEMENT_ROWS_PER_PAGE + MANAGEMENT_ROWS_PER_PAGE
  );

  const handleCompanyUpdated = async (companyId) => {
    invalidateApiQueries(API_QUERY_KEYS.companyList);
    await loadCompanies(companyId);
  };

  const handleCompanyCreated = async () => {
    invalidateApiQueries(API_QUERY_KEYS.companyList);
    await loadCompanies();
    setIsCreateOpen(false);
    setSuccessMessage("会社を登録しました。");
  };

  const handleDeactivate = async (company) => {
    if (!canDeactivate) {
      return false;
    }

    setErrorMessage("");
    setSuccessMessage("");

    try {
      await postCompanyUpdate(buildCompanyDeactivatePayload(company));
      invalidateApiQueries(API_QUERY_KEYS.companyList);
      await loadCompanies();
      setSuccessMessage("会社を削除しました。");
      return true;
    } catch (error) {
      handleApiError(error);
      return false;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: 240 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isDetailOpen && selectedCompany) {
    return (
      <CompanyDetail
        company={selectedCompany}
        role={role}
        onBack={() => setIsDetailOpen(false)}
        onUpdated={handleCompanyUpdated}
        onForbidden={onForbidden}
      />
    );
  }

  if (isCreateOpen && role === "super_admin") {
    return (
      <CompanyCreate
        role={role}
        onBack={() => setIsCreateOpen(false)}
        onCreated={handleCompanyCreated}
      />
    );
  }

  return (
    <ManagementListPage
      title="会社一覧"
      count={companies.length}
      action={
        role === "super_admin" && (
          <Button
            variant="contained"
            startIcon={<AddBusinessIcon />}
            onClick={() => {
              setErrorMessage("");
              setSuccessMessage("");
              setIsCreateOpen(true);
            }}
          >
            会社を新規登録
          </Button>
        )
      }
    >
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      {companies.length === 0 && !errorMessage ? (
        <ManagementEmptyState>表示できる会社はありません。</ManagementEmptyState>
      ) : (
        <ManagementTableContainer>
          <Table sx={{ ...MANAGEMENT_TABLE_SX, minWidth: canDeactivate ? 860 : 780 }} aria-label="会社一覧">
          <colgroup>
            <col style={{ width: 72 }} />
            <col style={{ width: 320 }} />
            <col style={{ width: 220 }} />
            <col style={{ width: 88 }} />
            <col />
            <col style={{ width: 72 }} />
            {canDeactivate && <col style={{ width: 72 }} />}
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell align="right" sx={MANAGEMENT_HEADER_CELL_SX}>ID</TableCell>
              <TableCell sx={MANAGEMENT_HEADER_CELL_SX}>会社名</TableCell>
              <TableCell sx={MANAGEMENT_HEADER_CELL_SX}>更新日時</TableCell>
              <TableCell align="center" sx={MANAGEMENT_HEADER_CELL_SX}>状態</TableCell>
              <TableCell aria-hidden="true" sx={MANAGEMENT_SPACER_HEADER_CELL_SX} />
              <TableCell align="center" sx={MANAGEMENT_HEADER_CELL_SX}>編集</TableCell>
              {canDeactivate && (
                <TableCell align="center" sx={MANAGEMENT_HEADER_CELL_SX}>削除</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedCompanies.map((company) => (
              <TableRow key={company.companyId} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                <TableCell align="right" sx={MANAGEMENT_BODY_CELL_SX}>{company.companyId}</TableCell>
                <TableCell sx={MANAGEMENT_BODY_CELL_SX}>
                  <ManagementTruncatedText>{company.companyName}</ManagementTruncatedText>
                </TableCell>
                <TableCell sx={{ ...MANAGEMENT_BODY_CELL_SX, whiteSpace: "nowrap" }}>
                  {company.updatedAt ?? "－"}
                </TableCell>
                <TableCell align="center" sx={MANAGEMENT_BODY_CELL_SX}>
                  <Chip
                    label={company.isActive === false ? "無効" : "有効"}
                    color={company.isActive === false ? "default" : "success"}
                    size="small"
                  />
                </TableCell>
                <TableCell aria-hidden="true" sx={MANAGEMENT_SPACER_BODY_CELL_SX} />
                <TableCell align="center" sx={MANAGEMENT_ACTION_CELL_SX}>
                  <ManagementIconActionButton
                    aria-label={`${company.companyName}を編集`}
                    tooltip="編集"
                    onClick={() => {
                      setSelectedCompany(company);
                      setIsDetailOpen(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </ManagementIconActionButton>
                </TableCell>
                {canDeactivate && (
                  <TableCell align="center" sx={MANAGEMENT_ACTION_CELL_SX}>
                    <CompanyDeleteDialog company={company} onDelete={handleDeactivate} />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <ManagementListPagination
          count={companies.length}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
        />
        </ManagementTableContainer>
      )}
    </ManagementListPage>
  );
}

export default CompanyList;
